"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FileSpreadsheet, FileText } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";

interface RevenueDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalCash: number;
      avgRevenuePerWash: number;
      highestDay: {
        date: string;
        revenue: number;
      };
      lowestDay: {
        date: string;
        revenue: number;
      };
    };
    revenueVsRequests: Array<{
      date: string;
      revenue: number;
      requests: number;
    }>;
  };
}

export function RevenueDashboard({ data }: RevenueDashboardProps) {

  const getExportData = () => {
    return data.revenueVsRequests.map((item) => ({
      Date: item.date,
      Revenue: item.revenue,
      Requests: item.requests,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Revenue_Report", "Revenue");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Revenue_Report", {
      title: "Revenue Report",
      subtitle: "Revenue vs Requests analysis",
      dateRange: data.dateRange,
    });
  };

  return (
    <div className="space-y-4">
      {/* Daily Revenue Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Cash Collected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.summary.totalCash)}
            </div>
            <p className="text-xs text-muted-foreground">
              Cash payments in period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Revenue per Wash</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.summary.avgRevenuePerWash)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average transaction value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Highest Day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.summary.highestDay.revenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.summary.highestDay.date}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Lowest Day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.summary.lowestDay.revenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.summary.lowestDay.date}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue vs Requests Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Revenue vs Requests</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data.revenueVsRequests}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                stroke="#0ea5e9"
                name="Revenue (QAR)"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="requests"
                stroke="#8b5cf6"
                name="Requests"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Insight:</strong> This chart helps identify correlation
              between appointment requests and cash collected. Large gaps may
              indicate discount impact, subscription cannibalization, or revenue
              leakage.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

